// Décodeur de panic iPhone (.ips et anciens panic-full).
//
// Un .ips moderne = 1re ligne JSON (en-tête) + corps JSON. On extrait le modèle,
// la version OS et la chaîne de panic, puis on mappe des SIGNATURES connues vers
// des pistes board-level (sous-système → composants/rails à suspecter).
//
// ⚠️ Heuristiques : un panic logiciel n'implique pas toujours une panne matérielle.
// Les pistes sont des orientations, pas un diagnostic certain.

import type { Domaine } from "../domain/types";

export interface PanicHint {
  subsystem: string; // ce que pointe le panic
  piste: string; // orientation board-level
  domaine: Domaine;
}

export interface PanicReport {
  product: string | null; // identifiant matériel, ex. iPhone12,1
  productName: string | null; // nom commercial, ex. iPhone 11
  osVersion: string | null;
  bugType: string | null;
  panicString: string; // texte de panic (tronqué pour l'affichage si besoin)
  hints: PanicHint[];
  rawKeys: string[]; // champs trouvés (debug)
}

// Quelques identifiants matériels courants → nom commercial.
const MODELS: Record<string, string> = {
  "iPhone10,1": "iPhone 8", "iPhone10,2": "iPhone 8 Plus", "iPhone10,3": "iPhone X",
  "iPhone10,4": "iPhone 8", "iPhone10,5": "iPhone 8 Plus", "iPhone10,6": "iPhone X",
  "iPhone11,2": "iPhone XS", "iPhone11,4": "iPhone XS Max", "iPhone11,6": "iPhone XS Max",
  "iPhone11,8": "iPhone XR",
  "iPhone12,1": "iPhone 11", "iPhone12,3": "iPhone 11 Pro", "iPhone12,5": "iPhone 11 Pro Max",
  "iPhone12,8": "iPhone SE (2e gén.)",
  "iPhone13,1": "iPhone 12 mini", "iPhone13,2": "iPhone 12", "iPhone13,3": "iPhone 12 Pro",
  "iPhone13,4": "iPhone 12 Pro Max",
  "iPhone14,2": "iPhone 13 Pro", "iPhone14,3": "iPhone 13 Pro Max", "iPhone14,4": "iPhone 13 mini",
  "iPhone14,5": "iPhone 13", "iPhone14,6": "iPhone SE (3e gén.)", "iPhone14,7": "iPhone 14",
  "iPhone14,8": "iPhone 14 Plus",
  "iPhone15,2": "iPhone 14 Pro", "iPhone15,3": "iPhone 14 Pro Max", "iPhone15,4": "iPhone 15",
  "iPhone15,5": "iPhone 15 Plus",
  "iPhone16,1": "iPhone 15 Pro", "iPhone16,2": "iPhone 15 Pro Max",
};

// Signatures (regex sur la chaîne de panic) → piste board-level.
const SIGNATURES: { re: RegExp; hint: PanicHint }[] = [
  {
    re: /ANS2?|AppleNANDController|NVMe|nand|smartio/i,
    hint: { subsystem: "Stockage (NAND / ANS)", domaine: "board",
      piste: "Lignes NAND / contrôleur de stockage. Vérifier soudures NAND (reball), alimentation du NAND, lignes data." },
  },
  {
    re: /AppleSMC|SMC|gpmu|PMU|pmu_|spmi/i,
    hint: { subsystem: "Gestion d'alimentation (PMU/SMC/SPMI)", domaine: "power",
      piste: "PMIC / lignes SPMI. Vérifier les rails du PMIC et les condensateurs en court à proximité." },
  },
  {
    re: /thermal|temperature|overtemp/i,
    hint: { subsystem: "Thermique", domaine: "power",
      piste: "Surchauffe d'un rail/composant. Caméra thermique au branchement pour localiser le point chaud." },
  },
  {
    re: /wifi|bluetooth|BCM|wlan|AppleBCMWLAN/i,
    hint: { subsystem: "Wi-Fi / Bluetooth", domaine: "board",
      piste: "Module/PA Wi-Fi-BT. Vérifier alimentation du module et soudures (reball/remplacement)." },
  },
  {
    re: /baseband|mav[e]?rick|bbctl|AppleBaseband/i,
    hint: { subsystem: "Baseband (modem)", domaine: "board",
      piste: "Baseband / alimentation modem. Vérifier les rails baseband et le PMIC baseband." },
  },
  {
    re: /gpu|AGX|sgx|display|backlight/i,
    hint: { subsystem: "GPU / Affichage", domaine: "screen",
      piste: "Étage graphique/affichage. Vérifier rails d'affichage et rétroéclairage." },
  },
  {
    re: /watchdog|wdt|0xBAADCA[FE0-9]+|leg[01]|SOCD/i,
    hint: { subsystem: "Watchdog / blocage SoC", domaine: "board",
      piste: "Le SoC ne répond plus. Souvent alim/clock instable : vérifier rails core et oscillateurs." },
  },
];

const MODEL_RE = /\b(iPhone\d{1,2},\d{1,2})\b/;

function pick(obj: Record<string, unknown>, ...keys: string[]): string | null {
  for (const k of keys) {
    const v = obj[k];
    if (typeof v === "string" && v.trim()) return v.trim();
  }
  return null;
}

export function decodePanic(text: string): PanicReport | null {
  const trimmed = text.trim();
  if (!trimmed) return null;

  let header: Record<string, unknown> = {};
  let body: Record<string, unknown> = {};

  // Format .ips : 1re ligne JSON (en-tête) + reste JSON (corps).
  const nl = trimmed.indexOf("\n");
  if (trimmed.startsWith("{") && nl > 0) {
    try {
      header = JSON.parse(trimmed.slice(0, nl));
    } catch {
      /* en-tête non-JSON : on ignore */
    }
    try {
      body = JSON.parse(trimmed.slice(nl + 1));
    } catch {
      /* corps non-JSON */
    }
  } else if (trimmed.startsWith("{")) {
    try {
      body = JSON.parse(trimmed);
    } catch {
      /* texte brut */
    }
  }

  const merged = { ...body, ...header };
  const rawKeys = Object.keys(merged);

  // panicString : champ direct, ou imbriqué, ou tout le texte en dernier recours.
  let panicString =
    pick(merged, "panicString", "panic_string") ??
    (typeof (body as { panicString?: unknown }).panicString === "string"
      ? (body as { panicString: string }).panicString
      : "") ??
    "";
  if (!panicString) {
    // Ancien panic-full ou champ inconnu : on prend le texte brut.
    panicString = trimmed.length > 4000 ? trimmed.slice(0, 4000) + "…" : trimmed;
  }

  const product = pick(merged, "product", "model") ?? (MODEL_RE.exec(trimmed)?.[1] ?? null);
  const osVersion =
    pick(merged, "os_version", "osVersion", "build") ??
    (/\b(\d{1,2}\.\d(?:\.\d)?)\b/.exec(panicString)?.[1] ?? null);
  const bugType = pick(merged, "bug_type", "bugType");

  const hints: PanicHint[] = [];
  for (const s of SIGNATURES) {
    if (s.re.test(panicString) && !hints.some((h) => h.subsystem === s.hint.subsystem)) {
      hints.push(s.hint);
    }
  }

  return {
    product,
    productName: product ? (MODELS[product] ?? null) : null,
    osVersion,
    bugType,
    panicString: panicString.length > 1500 ? panicString.slice(0, 1500) + "…" : panicString,
    hints,
    rawKeys,
  };
}
